import React, {FC} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {FrameTypesEnum} from '../../../../../libs/shared/core/src/models/api/apiTypes';
import {PaletteColorsEnum} from '../../../../../libs/shared/core/src/models/api/paletteModel';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {entryPointIsInboxSidePanelEntryPoint} from '../../../../models/api/applicationEntryPointModel';
import {ApplicationModel} from '../../../../models/api/applicationModel';
import {IntegrationTypeModel} from '../../../../models/api/integrationTypesModel';
import {SidePanelFrame} from '../../../../store/stateless/sidePanelFrameSelectors';
import {ApplicationCard} from '../../../common/applications/applicationCard';
import {ApplicationCardIconAvatar} from '../../../common/applications/applicationCardIconAvatar';
import {ApplicationCardImageAvatar} from '../../../common/applications/applicationCardImageAvatar';
import {ApplicationCardInitialsAvatar} from '../../../common/applications/applicationCardInitialsAvatar';
import {ApplicationIcon} from '../../../common/applications/applicationIcon';

/*
 * Props.
 */

interface SidePanelApplicationFrameCardProps {
  frame: SidePanelFrame;
  application?: ApplicationModel;
  integrationType?: IntegrationTypeModel;
  isPinned?: boolean;
  onPinnedChange: () => void;
}

/*
 * Component.
 */

export const SidePanelApplicationFrameCard: FC<SidePanelApplicationFrameCardProps> = (props) => {
  const {frame, isPinned, integrationType, application, onPinnedChange} = props;
  return (
    <ApplicationCard
      avatar={renderApplicationAvatar(frame)}
      name={renderNameForFrame(frame)}
      description={renderDescriptionForFrame(frame, integrationType, application)}
      isPinned={isPinned}
      onPinnedChange={onPinnedChange}
    />
  );
};

/*
 * Helpers.
 */

function renderApplicationAvatar(frame: SidePanelFrame) {
  switch (frame.frameType) {
    case FrameTypesEnum.CALENDAR:
      return <ApplicationCardIconAvatar iconName="calendar" color={PaletteColorsEnum.RED} />;
    case FrameTypesEnum.CUSTOMER_VIEW:
      return <ApplicationCardIconAvatar iconName="contacts" color={frame.color} />;
    case FrameTypesEnum.KNOWLEDGE_BASE:
      return <ApplicationCardIconAvatar iconName="knowledgeBaseFilled" color={frame.color} />;
    case FrameTypesEnum.START_GUIDE:
      return (
        <UserThemeConsumer>
          {(theme) => <ApplicationCardIconAvatar iconName="questionMark" color={theme.greys.shade90} />}
        </UserThemeConsumer>
      );
    case FrameTypesEnum.KEYBOARD_HELP:
      return <ApplicationCardIconAvatar iconName="keyboard" color={PaletteColorsEnum.GREY} />;
    case FrameTypesEnum.ENTRY_POINT: {
      const {iconBaseUrl} = frame;
      if (iconBaseUrl) {
        return <ApplicationCardImageAvatar url={iconBaseUrl} />;
      }
      return (
        <ApplicationCardInitialsAvatar name={frame.name} color={frame.color} size={VisualSizesEnum.LARGE} />
      );
    }
    case FrameTypesEnum.INTEGRATION:
      return <ApplicationCardImageAvatar url={frame.iconsUrl} />;
    case FrameTypesEnum.APPLICATION_ENTRY_POINT:
      if (
        frame.frameType === FrameTypesEnum.APPLICATION_ENTRY_POINT &&
        !entryPointIsInboxSidePanelEntryPoint(frame)
      ) {
        return null;
      }

      return <ApplicationIcon applicationId={frame.applicationId} size={VisualSizesEnum.EXTRA_LARGE} />;
    default:
      return null;
  }
}

function renderNameForFrame(frame: SidePanelFrame) {
  switch (frame.frameType) {
    case FrameTypesEnum.CALENDAR:
      return <Trans id="sidepanel-applications-manager-calendar-title">Calendar</Trans>;
    case FrameTypesEnum.CUSTOMER_VIEW:
      return <Trans id="sidepanel-applications-manager-contacts-title">Contacts</Trans>;
    case FrameTypesEnum.KNOWLEDGE_BASE:
      return <Trans id="sidepanel-applications-manager-knowledge-base-title">Knowledge base</Trans>;
    case FrameTypesEnum.START_GUIDE:
      return <Trans id="sidepanel-applications-manager-start-guide-title">Help &amp; tips</Trans>;
    case FrameTypesEnum.KEYBOARD_HELP:
      return <Trans id="sidepanel-applications-manager-shortcuts-title">Shortcuts</Trans>;
    default:
      return frame.name;
  }
}

function renderDescriptionForFrame(
  frame: SidePanelFrame,
  integrationType?: IntegrationTypeModel,
  application?: ApplicationModel,
) {
  // If we have an integration type, we should already have a description to render.
  if (integrationType) {
    return integrationType.description;
  }
  switch (frame.frameType) {
    case FrameTypesEnum.CALENDAR:
      return (
        <Trans id="sidepanel-applications-manager-calendar-description">
          View your agenda, schedule new meetings, and manage scheduling links
        </Trans>
      );
    case FrameTypesEnum.CUSTOMER_VIEW:
      return (
        <Trans id="sidepanel-applications-manager-contacts-description">
          View details about the accounts and contacts involved in your conversations
        </Trans>
      );
    case FrameTypesEnum.KNOWLEDGE_BASE:
      return (
        <Trans id="sidepanel-applications-manager-knowledge-base-description">
          Easily view and find Front knowledge base content.
        </Trans>
      );
    case FrameTypesEnum.START_GUIDE:
      return (
        <Trans id="sidepanel-applications-manager-start-guide-description">
          Learn more about how to get the most out of Front
        </Trans>
      );
    case FrameTypesEnum.KEYBOARD_HELP:
      return (
        <Trans id="sidepanel-applications-manager-shortcuts-description">
          View shortcuts to perform common actions in Front more quickly.
        </Trans>
      );
    case FrameTypesEnum.APPLICATION_ENTRY_POINT: {
      if (!application || !application.description) {
        return null;
      }
      return application.description;
    }
    case FrameTypesEnum.ENTRY_POINT:
    default:
      return null;
  }
}
